import {useBooleanState} from '@wandb/weave/common/util/hooks';
import {Button} from '@wandb/weave/components/Button';
import {Radio} from '@wandb/weave/components/Radio';
import React, {useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  useAutoOrganizePrefix,
  useShouldAutoGeneratePanels,
  useUpdateLayoutSettingAndOrganizePrefix,
} from '../hooks';
import {OrganizationPrefix} from '../types';
import {getWorkspaceLayoutSettingWithDefaults} from './getWorkspaceLayoutSettings';

export const WorkspaceLayoutAutoOrganizePrefix = () => {
  const autoOrganizePrefixUndefaulted = useAutoOrganizePrefix();
  const shouldAutoGeneratePanelsUndefaulted = useShouldAutoGeneratePanels();
  const updateLayoutSettingAndOrganizePrefix =
    useUpdateLayoutSettingAndOrganizePrefix();
  const {autoOrganizePrefix, shouldAutoGeneratePanels} = useMemo(
    () =>
      getWorkspaceLayoutSettingWithDefaults({
        autoOrganizePrefix: autoOrganizePrefixUndefaulted,
        shouldAutoGeneratePanels: shouldAutoGeneratePanelsUndefaulted,
      }),
    [autoOrganizePrefixUndefaulted, shouldAutoGeneratePanelsUndefaulted]
  );

  const {
    state: isProtectedSettingsLocked,
    toggle: toggleProtectedSettingsLock,
  } = useBooleanState(true);

  if (shouldAutoGeneratePanels === 'pending') {
    return null;
  }

  return (
    <ProtectedSetting
      isProtectedSettingsLocked={isProtectedSettingsLocked}
      shouldAutoGeneratePanels={shouldAutoGeneratePanels}
      toggleProtectedSettingsLock={toggleProtectedSettingsLock}>
      <fieldset className="text-wb-text-primary">
        <Radio.Root
          aria-label="Grouping sections by prefix"
          name="Organize panels"
          disabled={isProtectedSettingsLocked}
          onValueChange={value => {
            const numVal = Number(value);
            updateLayoutSettingAndOrganizePrefix(numVal);
          }}
          value={autoOrganizePrefix.toString()}>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="organize-panels-first-prefix"
              value={OrganizationPrefix.FirstPrefix.toString()}>
              <Radio.Indicator />
            </Radio.Item>
            <label htmlFor="organize-panels-first-prefix">
              <span className="mx-8">Group first prefix</span>
              <span className="text-wb-text-tertiary">
                e.g. metric "a/b/c/d" → section a
              </span>
            </label>
          </div>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="organize-panels-last-prefix"
              value={OrganizationPrefix.LastPrefix.toString()}>
              <Radio.Indicator />
            </Radio.Item>
            <label
              htmlFor="organize-panels-last-prefix"
              className="align-center flex">
              <span className="mx-8">Group last prefix</span>
              <span className="text-wb-text-tertiary">
                e.g. metric "a/b/c/d" → section a/b/c
              </span>
            </label>
          </div>
        </Radio.Root>
      </fieldset>
    </ProtectedSetting>
  );
};

const ProtectedSetting = ({
  isProtectedSettingsLocked,
  shouldAutoGeneratePanels,
  toggleProtectedSettingsLock,
  children,
}: {
  isProtectedSettingsLocked: boolean;
  shouldAutoGeneratePanels: boolean;
  toggleProtectedSettingsLock: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div
      className="rounded bg-wb-surface-tint px-16 py-12"
      data-test="workspace-layout-section-organization">
      <div className="mb-8 flex justify-between gap-10">
        <div>
          <p className="text-base font-semibold leading-8">
            Section organization
          </p>
          <p className="text-sm text-wb-text-tertiary">
            {shouldAutoGeneratePanels
              ? 'All existing sections and panels will be removed, and your workspace will be repopulated with updated section names.'
              : 'All existing sections and panels will be removed.'}
          </p>
        </div>
        <Button
          icon={isProtectedSettingsLocked ? 'lock-closed' : 'lock-open'}
          variant="secondary"
          onClick={toggleProtectedSettingsLock}
        />
      </div>
      <div
        className={twMerge(
          'transition-opacity',
          isProtectedSettingsLocked && 'opacity-30'
        )}>
        {children}
      </div>
    </div>
  );
};
